import { Property, Survey } from "@/features/survey/types/survey.types";
import ButtonComments from "@/shared/components/ButtonComments";
import ShortlistToggleButton from "@/shared/components/ButtonShortlist";
import { hasAddressLine2AsCustom, renderUrlOrValue } from "@/shared/utils";
import {
  getActivePropertyFields,
  isFieldVisible,
  PropertyFieldRule,
} from "@/shared/utils/propertyFieldRules";
import { getPropertyValue } from "@/shared/utils/propertyUtils";
import { ReactNode } from "react";

interface PropertyDetailsProps {
  property: Property;
  currentSurvey: Survey;
}

const PropertyDetails = ({ property, currentSurvey }: PropertyDetailsProps) => {
  const visibleFields = getActivePropertyFields(currentSurvey, {
    excludeAddressFields: true,
    additionalExcludedFields: ["comments", "amenities"],
    hasAddressLine2Custom: hasAddressLine2AsCustom(currentSurvey),
  });

  // Format field values for display
  const formatFieldValue = (
    value: unknown,
    fieldKey?: string | null,
  ): ReactNode => {
    if (value === undefined || value === null || value === "") {
      return "—";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      // Format currency values for fields that typically contain currency
      const currencyFields = [
        "askingRate",
        "opex",
        "ti",
        "totalCost",
        "price",
        "marketValue",
      ];
      const isCurrency = fieldKey && currencyFields.includes(fieldKey);

      // Format as currency if it seems to be a currency field or a large number
      if (isCurrency || value > 10000) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(value);
      }

      // Format area fields with commas and SF suffix
      const areaFields = [
        "availableSF",
        "rentableSF",
        "minDivisibleSF",
        "maxContiguousSF",
        "totalSF",
        "squareFeet",
      ];
      if (fieldKey && areaFields.includes(fieldKey)) {
        return `${new Intl.NumberFormat("en-US").format(value)} SF`;
      }

      return value.toString();
    }

    if (typeof value === "string") {
      return renderUrlOrValue(value, fieldKey);
    }

    // If it's an array, join with commas
    if (Array.isArray(value)) {
      return value.join(", ");
    }

    // For all other types, convert to string
    return String(value);
  };

  // Use all visible fields returned by getActivePropertyFields, but filter out fields without valid fieldLabel
  const propertyDetailsFields = visibleFields.filter(
    (field: PropertyFieldRule) => {
      return field.fieldLabel && field.fieldLabel.trim() !== "";
    },
  );

  // Get comments and amenities values from property
  const commentsValue = getPropertyValue("comments", "Comments", property);
  const amenitiesValue = getPropertyValue("amenities", "Amenities", property);

  if (propertyDetailsFields.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl lg:text-2xl font-serif text-primary">
          Property Details
        </h2>
        <div className="flex items-center gap-3 action-buttons">
          <ShortlistToggleButton
            property={property}
            size={window.innerWidth < 1250 ? "Small" : undefined}
          />
          <ButtonComments
            property={property}
            size={window.innerWidth < 1250 ? "Small" : undefined}
          />
        </div>
      </div>

      {/* Details List */}
      <div className="flex flex-col">
        {propertyDetailsFields.map(
          (field: PropertyFieldRule, index: number) => {
            const fieldKey = field.fieldKey || "";
            const fieldId = field.fieldId || "";
            // Only try to get property value if fieldKey exists, otherwise show "—"
            const value = getPropertyValue(fieldKey, fieldId, property);
            const formattedValue = formatFieldValue(value, fieldKey);

            return (
              <div
                key={field.fieldId || fieldKey}
                className={`grid grid-cols-2 items-start gap-4 px-4 py-2 font-normal text-data text-sm leading-5 ${
                  index % 2 === 0 ? "bg-secondary-lightest" : "bg-white"
                }`}
              >
                <span className="text-left">{field.fieldLabel}</span>
                <div className="text-sm text-left leading-relaxed whitespace-pre-line">
                  {formattedValue}
                </div>
              </div>
            );
          },
        )}
      </div>

      {/* Comments Section */}
      {commentsValue && isFieldVisible(currentSurvey, "comments") && (
        <div className="pt-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments</h3>
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            <p>{commentsValue}</p>
          </div>
        </div>
      )}

      {/* Amenities Section */}
      {amenitiesValue && isFieldVisible(currentSurvey, "amenities") && (
        <div className="pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Amenities
          </h3>
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            <p>{amenitiesValue}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
